import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import If from '@components/If';
import useUserUid from '@core/hooks/useUserUid';
import { AuthContext } from '@core/providers/AuthProvider';
import {
  clearPendingAppSwitchOrganizationId,
  getPendingAppSwitchOrganizationId,
} from '@core/utils/appSwitch';
import useUserOrganizations from '../hooks/useUserOrganizations';
import OrganizationPicker from './OrganizationPicker';

const OrganizationSetter = ({ children }: PropsWithChildren<unknown>) => {
  const { organization, setOrganization, isAcquiring: isGettingToken } = useContext(AuthContext);
  const userUid = useUserUid();
  // Organization handed over by the other app version on a v2 <-> v3 switch.
  // It is applied before rendering anything org-scoped (AppVersionGuard
  // included) so both apps resolve settings for the same organization.
  const [pendingOrganizationId, setPendingOrganizationId] = useState(
    getPendingAppSwitchOrganizationId,
  );
  const { organizations, isFetched: isOrganizationsFetched } = useUserOrganizations(
    pendingOrganizationId ? userUid : undefined,
  );

  useEffect(() => {
    if (!pendingOrganizationId || !isOrganizationsFetched) {
      return;
    }

    clearPendingAppSwitchOrganizationId();
    setPendingOrganizationId(null);

    // Only switch to organizations the user actually belongs to — exchanging a
    // product token for any other org fails and logs the user out.
    const belongsToOrganization = organizations.some(
      ({ organizationProductId }) => organizationProductId === pendingOrganizationId,
    );
    if (belongsToOrganization && organization?.organizationId !== pendingOrganizationId) {
      setOrganization({
        organizationId: pendingOrganizationId,
        hasMoreOrganizations: organizations.length > 1,
      });
    }
  }, [
    pendingOrganizationId,
    isOrganizationsFetched,
    organizations,
    organization?.organizationId,
    setOrganization,
  ]);

  const hasOrg = !!organization?.organizationId;

  if (pendingOrganizationId) {
    return null;
  }

  return (
    <If
      condition={hasOrg}
      fallback={<OrganizationPicker onOrganizationPicked={(info) => setOrganization(info)} />}
    >
      <If condition={!isGettingToken}>{children}</If>
    </If>
  );
};

export default OrganizationSetter;
